import type React from "react";
import { useState, useRef, useEffect } from "react";
import logo from "../../assets/images/logo.png";
import { useNavigate } from "react-router-dom";
import type { User } from "../../types/user";
import { handleLogoutGenerator } from "../../global-actions/handleLogout";
import { auth } from "../../firebase/firebase-config";
import { useDispatch } from "react-redux";
import { setIsAdminView } from "../../store/authSlice";
import NotificationBell from "./NotificationBell";
import { LogOut, Settings, HelpCircle, UserIcon, BarChart3 } from "lucide-react";
import { useImpersonation } from "../../hooks/useImpersonation";

interface TopBarProps {
    isAdmin: boolean;
    user: User | null;
    showButton?: boolean;
    buttonTitle?: string;
    onButtonClick?: () => void;
    isAdminView: boolean;
}

const TopBar: React.FC<TopBarProps> = ({
    isAdmin,
    user,
    buttonTitle,
    showButton,
    onButtonClick,
    isAdminView,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isImpersonated = useImpersonation();

    const handleLogoClick = () => {
        isAdminView ? navigate("/admin") : navigate("/");
    };

    const handleAdmin = () => {
        dispatch(setIsAdminView(true));
        sessionStorage.setItem("isAdminView", "true");
        navigate("/admin");
        setIsDropdownOpen(false);
    };

    const handleUser = () => {
        dispatch(setIsAdminView(false));
        sessionStorage.setItem("isAdminView", "false");
        navigate("/");
        setIsDropdownOpen(false);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen((prev) => !prev);
    };

    // Close dropdown when interacting outside (click, touch, scroll)
    useEffect(() => {
        const handleInteractionOutside = (event: Event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleInteractionOutside);
            document.addEventListener("touchstart", handleInteractionOutside);
            window.addEventListener("scroll", handleInteractionOutside, true);
        }

        return () => {
            document.removeEventListener("mousedown", handleInteractionOutside);
            document.removeEventListener(
                "touchstart",
                handleInteractionOutside
            );
            window.removeEventListener(
                "scroll",
                handleInteractionOutside,
                true
            );
        };
    }, [isDropdownOpen]);

    const handleLogout = handleLogoutGenerator(auth, navigate, user);

    return (
        <div className="flex items-center justify-between px-4 md:px-6 py-2 bg-white w-full h-full shadow-sm">
            {/* Logo Section */}
            <div
                id="logo-btn"
                className="flex items-center space-x-2 cursor-pointer"
                onClick={handleLogoClick}
            >
                <img
                    src={logo || "/placeholder.svg"}
                    alt="Marabot Logo"
                    className="h-8 md:h-10"
                />
            </div>

            {/* Conditional Button - Hidden on Mobile */}
            {showButton && (
                <div className="hidden md:block">
                    <button
                        onClick={onButtonClick}
                        className="px-4 py-2 bg-[#193456] text-white rounded-md shadow-sm hover:bg-opacity-90 transition"
                    >
                        {buttonTitle}
                    </button>
                </div>
            )}

            {/* User Account Section */}
            <div className="flex items-center space-x-2 md:space-x-4">
                {/* Notification Icon */}
                <NotificationBell />

                {/* User Profile Icon */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        id="profile-btn"
                        onClick={toggleDropdown}
                        className="flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full focus:outline-none focus:ring-2 focus:ring-[#F0DFAF] transition-all bg-[#E9C468] text-[#193456]"
                        aria-haspopup="true"
                        aria-expanded={isDropdownOpen}
                    >
                        <span className="text-base md:text-lg font-semibold">
                            {user &&
                                user.nameFirst &&
                                user.nameLast &&
                                user.nameFirst[0].toUpperCase() +
                                    user.nameLast[0].toUpperCase()}
                        </span>
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                        <div
                            className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden z-50"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-4 py-3 border-b border-gray-100">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user?.nameFirst} {user?.nameLast}
                                </p>
                                <p className="text-xs text-gray-500 truncate mt-1">
                                    {user?.email}
                                </p>
                            </div>
                            <ul>
                                <li>
                                    {(isAdmin || user?.adminRole === "CREATOR") && !isAdminView  && !isImpersonated ? (
                                        <button
                                            id="admin-mode-btn"
                                            className="w-full text-left px-4 py-2 flex items-center text-sm text-[#193456] hover:bg-[#F0DFAF]"
                                            onClick={handleAdmin}
                                        >
                                            <UserIcon className="w-4 h-4 mr-2" />
                                            Admin Mode
                                        </button>
                                    ) : (isAdmin || user?.adminRole === "CREATOR")&& !isImpersonated ? (
                                        <button
                                            id="user-mode-btn"
                                            className="w-full text-left px-4 py-2 flex items-center text-sm text-[#193456] hover:bg-[#F0DFAF]"
                                            onClick={handleUser}
                                        >
                                            <UserIcon className="w-4 h-4 mr-2" />
                                            User Mode
                                        </button>
                                    ) : null}
                                </li>
                                {!isAdminView && (
                                    <>
                                        <li>
                                            <button
                                                id="analytics-btn"
                                                className="w-full text-left px-4 py-2 flex items-center text-sm text-[#193456] hover:bg-[#F0DFAF]"
                                                onClick={() => {
                                                    navigate("/analytics");
                                                    setIsDropdownOpen(false);
                                                }}
                                            >
                                                <BarChart3 className="w-4 h-4 mr-2" />
                                                Analytics
                                            </button>
                                        </li>

                                        <li>
                                            <button
                                                id="help-btn"
                                                className="w-full text-left px-4 py-2 flex items-center text-sm text-[#193456] hover:bg-[#F0DFAF]"
                                                onClick={() => {
                                                    navigate("/help");
                                                    setIsDropdownOpen(false);
                                                }}
                                            >
                                                <HelpCircle className="w-4 h-4 mr-2" />
                                                Help
                                            </button>
                                        </li>

                                        <li>
                                            <button
                                                id="settings-dropdown-btn"
                                                className="w-full text-left px-4 py-2 flex items-center text-sm text-[#193456] hover:bg-[#F0DFAF]"
                                                onClick={() => {
                                                    navigate("/settings");
                                                    setIsDropdownOpen(false);
                                                }}
                                            >
                                                <Settings className="w-4 h-4 mr-2" />
                                                Settings
                                            </button>
                                        </li>
                                    </>
                                )}
                                <li>
                                    <button
                                        id="logout-btn"
                                        className="w-full text-left px-4 py-2 flex items-center text-sm text-white bg-[#193456] hover:bg-opacity-90"
                                        onClick={handleLogout}
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Logout
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TopBar;
